import React, { useRef, useState, useCallback, useEffect } from "react";
import { render } from 'react-dom';
import { Transition } from 'react-transition-group';

(function init() {
  function loadComponent() {
    const rootElement = document.querySelector('[data-search]');
    if (rootElement) render(<SearchForm />, rootElement);
  }

  document.addEventListener("shopify:section:load", loadComponent);
  loadComponent();
})();

function SearchForm() {
  const [open, setOpen] = useState(null);
  const toggleOpen = useCallback(() => setOpen((open) => !open), []);

  const transitionRef = useRef(null);
  const trigger = useRef(null);
  const content = useRef(null);
  const elementToFocus = useRef(null);

  const defaultStyle = {
    transition: `opacity 300ms ease-in-out`,
    opacity: 0
  }

  const transitionStyles = {
    entering: { opacity: 1, visibility: 'visible' },
    entered:  { opacity: 1, visibility: 'visible' },
    exiting:  { opacity: 0, visibility: 'visible' },
    exited:  { opacity: 0, visibility: 'hidden' },
  };

  /*
    Event handlers
  */

  // If there's a click on the document and 1) search is open 2) the target is not the trigger, then close Search dropdown
  useEffect(() => {
    document.addEventListener("click", (e) => {
      const open = document.querySelector('[data-search-trigger][aria-expanded="true"]');
      if (typeof open != "undefined" && open != null) {
        if (
          !e.target.hasAttribute('data-search-trigger') &&
          e.target.closest('[data-search-parent]') === null
        ) {
          setOpen(false);
        }
      }
    });
  }, []);

  // If search is open and the escape key is pressed, close and return focus to trigger
  useEffect(() => {
    if (trigger) {
      document.addEventListener('keyup', (e) => {
        const open = trigger.current.getAttribute("aria-expanded") === "true";;
        if (e.key === 'Escape' && open) {
          trigger.current.focus();
          setOpen(false);
        }
      });
    }
  }, [trigger]);

  // If we are tabbing inside or outside of the dropdown, close it
  useEffect(() => {
    if (content) {
      content.current.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          const elements = content.current.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe");
          const first = elements[0];
          const last = elements[elements.length - 1];

          if (e.target === last && !e.shiftKey) setOpen(false); 
          if (e.target === first && e.shiftKey) setOpen(false);
        }
      })
    }
  }, [content]);

  useEffect(() => {
    if (open) {
      document.getElementById("SearchOverlay").classList.remove('opacity-0', 'invisible');
    } else {
      document.getElementById("SearchOverlay").classList.add('opacity-0', 'invisible');
    }
  }, [open])

  return (
    <div
      ref={content}
      className="h-full"
      data-search-parent
    >
      <button 
        className="flex items-center h-full"
        aria-expanded={open}
        aria-controls={`Search-JS`}
        onClick={toggleOpen}
        data-search-trigger
        ref={trigger}
      >
        <svg aria-hidden="true" aria-focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="pointer-events-none">
          <path d="M18.031 16.617L22.314 20.899L20.899 22.314L16.617 18.031C15.0237 19.3082 13.042 20.0029 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20.0029 13.042 19.3082 15.0237 18.031 16.617ZM16.025 15.875C17.2941 14.5699 18.0029 12.8204 18 11C18 7.132 14.867 4 11 4C7.132 4 4 7.132 4 11C4 14.867 7.132 18 11 18C12.8204 18.0029 14.5699 17.2941 15.875 16.025L16.025 15.875Z" fill="currentColor"/>
        </svg>
        <span className="sr-only">Search</span>
      </button>

      <Transition transitionRef={transitionRef} in={open} timeout={500}>
        {state => (
          <div 
            ref={transitionRef} 
            style={{
              ...defaultStyle,
              ...transitionStyles[state]
            }}
            id={`Search-JS`}
            className={`absolute w-full bg-blush-100 z-10 left-0 top-full`}
          >
            <div className="md:max-w-[79rem] mx-auto md:px-[1rem] md:py-[3.25rem] border-t border-taupe-200">
              <form action="/search" className="md:flex justify-between gap-4">
                <div className="flex w-full max-w-full py-[1.75rem] md:py-0 px-4 md:px-0">
                  <input type="text"
                    ref={elementToFocus}
                    placeholder="Enter search term... "
                    name="q"
                    value=""
                    className="py-2 px-0 md:p-0 w-full border-none text-[1.125rem] md:text-[1.25rem] placeholder:text-gray-700 bg-blush-100"
                  />
                </div>
                <input type="hidden" name="type" value="product" />
                <input type="submit" value="Search" className="md:mt-0 text-center w-full md:w-auto bg-black text-white font-medium uppercase text-sm px-12 py-[1.125rem] grow-0 appearance-none rounded-none" />
              </form>
            </div>
          </div>
        )}
      </Transition>
    </div>
  )
}